import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatOption, MatSelect } from '@angular/material/select'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ChartConfiguration } from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, map, of, shareReplay, switchMap } from 'rxjs'
import { Client, ClientsService } from '../clients.service'
import { MetricsService, SlaProcessingDistributionBucket } from '../metrics.service'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { getColorPalette, isEqual } from '../utils'
import { themeColor } from '../theme-color'

const SELECTABLE_MONTH_COUNT = 12

const MONTH_NAME_KEYS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER']

export type SlaMonthOption = {
  value: string
  nameKey: string
  year: number
}

/**
 * Months are cut on UTC boundaries on the backend, so the options are built from UTC parts too. Deriving
 * them from the local date would offer a month the backend rejects for the couple of hours around a month
 * change when the two calendars disagree.
 */
export function buildSlaMonthOptions(now: Date): SlaMonthOption[] {
  return Array.from({ length: SELECTABLE_MONTH_COUNT }, (_unused, index) => {
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1))
    const year = monthStart.getUTCFullYear()
    const monthOfYear = monthStart.getUTCMonth() + 1
    return {
      value: `${year}-${String(monthOfYear).padStart(2, '0')}`,
      nameKey: MONTH_NAME_KEYS[monthStart.getUTCMonth()],
      year,
    }
  })
}

@Component({
  selector: 'app-sla-dashboard',
  standalone: true,
  imports: [
    AsyncPipe,
    BaseChartDirective,
    DecimalPipe,
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatLabel,
    MatOption,
    MatSelect,
    MultiFilterComponent,
    SpinnerComponent,
    ToolbarComponent,
    TranslatePipe,
  ],
  templateUrl: './sla-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sla-dashboard.component.scss',
})
export class SlaDashboardComponent {
  route = inject(ActivatedRoute)
  router = inject(Router)
  clientsService = inject(ClientsService)
  metricsService = inject(MetricsService)
  usersService = inject(UsersService)
  translateService = inject(TranslateService)

  monthOptions = buildSlaMonthOptions(new Date())

  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  currentUser$ = this.usersService.getCurrentUser().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data ?? null),
    filter((user) => user !== null),
    shareReplay(1)
  )

  isSupportUser$ = this.currentUser$.pipe(
    map((user) => user.role === 'SUPPORT'),
    shareReplay(1)
  )

  // Meetings belong to a client, so the figures are a client's. Only support may report on another one;
  // an administrator stays inside their own, which the endpoint enforces regardless.
  clients$ = this.isSupportUser$.pipe(
    switchMap((isSupportUser) =>
      isSupportUser
        ? this.clientsService.getClients().pipe(
            filter((result) => result.isSuccess),
            map((result) => result.data ?? [])
          )
        : of<Client[]>([])
    ),
    map((clients) => [...clients].sort((first, second) => first.name.localeCompare(second.name))),
    shareReplay(1)
  )

  // Deduped because the whole parameter map is upstream: a sort press must not re-run the month's figures.
  private selectedClientIdsFromUrl$ = this.route.queryParamMap.pipe(
    map((params) =>
      params
        .getAll('clientIds')
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id))
    ),
    distinctUntilChanged((previous, current) => isEqual(previous, current))
  )

  selectedClientIds$ = combineLatest([this.clients$, this.selectedClientIdsFromUrl$]).pipe(
    map(([clients, selectedIds]) => selectedIds.filter((id) => clients.some((client) => client.id === id)))
  )

  // An empty selection falls back to the viewer's own client rather than to every client: a support user
  // opening the page wants their own client's figures, not every client in the environment summed into one
  // meaningless total.
  clientIds$ = combineLatest([this.selectedClientIds$, this.currentUser$]).pipe(
    map(([selectedIds, user]) => (selectedIds.length > 0 ? selectedIds : [user.team.clientId])),
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
    shareReplay(1)
  )

  selectedMonth$ = this.route.queryParamMap.pipe(
    map((params) => {
      const month = params.get('month')
      return this.monthOptions.some((option) => option.value === month) ? month! : this.monthOptions[0].value
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  slaMetrics$ = combineLatest([this.clientIds$, this.selectedMonth$]).pipe(
    switchMap(([clientIds, month]) => this.metricsService.getSlaMetrics(clientIds, month)),
    shareReplay(1)
  )

  processingDistributionChart$ = this.slaMetrics$.pipe(
    filter((result) => result.isSuccess),
    map((result): ChartConfiguration<'bar'>['data'] => {
      const buckets = result.data?.processingDistribution ?? []
      return {
        labels: buckets.map((bucket, index) => this.describeDistributionBucket(buckets, bucket, index)),
        datasets: [
          {
            data: buckets.map((bucket) => bucket.meetingCount),
            label: this.translateService.instant('SLA_DASHBOARD.MEETINGS_AXIS'),
            backgroundColor: getColorPalette(1)[0],
          },
        ],
      }
    })
  )

  processingDistributionChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    scales: {
      x: { grid: { display: false }, ticks: { color: themeColor('loading-gray') } },
      y: { beginAtZero: true, grid: { color: themeColor('border') }, ticks: { precision: 0, color: themeColor('loading-gray') } },
    },
    plugins: {
      legend: { display: false },
    },
  }

  formatMinutes(durationInSeconds: number | null): string {
    if (durationInSeconds === null) {
      return '-'
    }
    return `${Math.round(durationInSeconds / 60)} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
  }

  displayClient = (client: Client): string => client.name

  displaySelectedClients(clients: Client[]) {
    return (ids: number[]) =>
      clients
        .filter((client) => ids.includes(client.id))
        .map((client) => client.name)
        .join(', ')
  }

  isExporting = false

  async exportToPdf() {
    // Guarded because rendering runs a browser server side: a double click would launch a second one for no
    // benefit, in a container that is already tight on memory.
    if (this.isExporting) {
      return
    }
    this.isExporting = true
    try {
      // Resolving the selection is inside the try as well: if that stream fails, the flag still has to clear
      // or the button stays disabled for the rest of the visit.
      const [clientIds, month] = await firstValueFrom(combineLatest([this.clientIds$, this.selectedMonth$]))
      await firstValueFrom(this.metricsService.exportSlaMetrics(clientIds, month), { defaultValue: undefined })
    } finally {
      this.isExporting = false
    }
  }

  async onMonthChange(month: string) {
    await this.router.navigate([], { queryParams: { month }, queryParamsHandling: 'merge', relativeTo: this.route })
  }

  async onClientChange(selectedIds: number[]) {
    await this.router.navigate([], {
      queryParams: selectedIds.length ? { clientIds: selectedIds } : { clientIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async onClientClear() {
    await this.router.navigate([], {
      queryParams: { clientIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  private describeDistributionBucket(buckets: SlaProcessingDistributionBucket[], bucket: SlaProcessingDistributionBucket, index: number): string {
    const lowerBound = index === 0 ? null : buckets[index - 1].upToSeconds
    if (lowerBound === null) {
      return `< ${this.formatBoundary(bucket.upToSeconds)}`
    }
    if (bucket.upToSeconds === null) {
      return `> ${this.formatBoundary(lowerBound)}`
    }
    return `${this.formatBoundary(lowerBound)} – ${this.formatBoundary(bucket.upToSeconds)}`
  }

  private formatBoundary(boundaryInSeconds: number | null): string {
    if (boundaryInSeconds === null) {
      return ''
    }
    if (boundaryInSeconds < 3600) {
      return `${boundaryInSeconds / 60} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
    }
    return `${boundaryInSeconds / 3600} ${this.translateService.instant('SLA_DASHBOARD.HOURS_ABBREVIATION')}`
  }
}
